import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Form, FormControl, FormLabel, Row } from "react-bootstrap";
import { faCircleInfo, faFile, faGlobe, faPlus, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import AppModal from "../../components/common/AppModal";
import { IJSTreeNode, IPageKeyword } from "../../../models/Interfaces";
import { ContentService } from "../../../services/ContentService";
import { KeywordService } from "../../../services/KeywordService";
import GenericTreeView, { INodeTypeDefinition } from "../../components/common/GenericTreeView";
import Loading from "../../components/common/Loading";
import { useConfirm } from "../../../config/ConfirmProvider";
import { useParams } from "react-router-dom";
import { EventBus } from "../../../helpers/EventBus";
import { createFormValidation } from "../../../helpers/formValidationHelper";
import { TreeItemSort } from "../../../config/Enums";
import AppEmptyState from "../../components/common/AppEmptyState";
import styles from "./pageKeywords.module.scss";

// Reusable function for validation configuration
const getValidationConfig = () => ({
  fields: {
    Keyword: {
      validators: {
        notEmpty: { message: "The keyword is required" },
        stringLength: {
          min: 3,
          max: 50,
          message: "The keyword must be between 3 and 50 characters long",
        },
      },
    },
  },
  plugins: {
    trigger: new Trigger(),
    bootstrap5: new Bootstrap5(),
  },
});

const PageKeywords = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [pages, setPages] = useState<Array<IJSTreeNode>>([]);
  const [selectedPage, setSelectedPage] = useState<IJSTreeNode | null>(null);
  const [keywords, setKeywords] = useState<Array<IPageKeyword>>([]);
  const [addKeyword, setAddKeyword] = useState<boolean>(false);
  const [selectedKeyword, setSelectedKeyword] = useState<IPageKeyword | null>(null);

  const contentSvcRef = useRef<ContentService | null>(null);
  const keywordSvcRef = useRef<KeywordService | null>(null);
  const formRefs = useRef<Record<string, HTMLFormElement | null>>({});
  const validatorsRef = useRef<Record<string, any>>({});
  const modalFormRef = useRef<HTMLFormElement | null>(null);

  const confirmation = useConfirm();
  const { pageParamId } = useParams();
  
  // Fetch pages and keywords
  const getPages = async (): Promise<void> => {
    if (contentSvcRef.current) {
      const pageres = await contentSvcRef.current.getPagesTreeData();
      if (pageres?.length > 0) {
        setPages(pageres);
      }
    }
  };

  const getKeywords = async (pageid: number): Promise<void> => {
    if (keywordSvcRef.current) {
      const kwres = await keywordSvcRef.current.getPageKeywords(pageid);
      setKeywords(kwres || []);
    }
  };

  const handlePageSelect = async (selected: IJSTreeNode): Promise<void> => {
    if (selected) {
      setSelectedPage(selected);
    }
  };

  const handleUpdateKeyword = (keyword: string, kwid: number): void => {
    if (kwid) {
      setKeywords(prev => prev.map(k =>
        k.Id === kwid ? { ...k, Keyword: keyword } : k
      ));
    }
  };

  const addNewKeyword = (): void => {
    if (keywordSvcRef.current && selectedPage) {
      const newkw = keywordSvcRef.current.newKeyword(selectedPage.id);
      if (newkw) {
        setSelectedKeyword(newkw);
        setAddKeyword(true);
      }
    }
  };

  const handleNewKeywordChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (selectedKeyword) {
      const value = event.target.value;
      setSelectedKeyword(prev => ({
        ...prev as IPageKeyword,
        Keyword: value,
      }));
    }
  };

  const cancelAddKeyword = (): void => {
    setAddKeyword(false);
    setSelectedKeyword(null);
  };

  const saveKeyword = async (id?: number): Promise<void> => {
    if (keywordSvcRef.current) {
      if (id) {
        const kw = keywords.find(k => k.Id === id);
        if (kw) {
          const res = await keywordSvcRef.current.saveKeyword(kw);
          if (res) {
            setKeywords(prev => prev.map(k => k.Id === res.Id ? res : k));
          }
        }
      } else if (selectedKeyword) {
        const newres = await keywordSvcRef.current.saveKeyword(selectedKeyword);
        if (newres) {
          setKeywords(prev => [
            ...prev,
            newres
          ]);
          cancelAddKeyword();
        }
      }
    }
  };

  const deleteKeyword = async (id: number): Promise<void> => {
    if (keywordSvcRef.current && selectedPage) {

      const confirmed = await confirmation.requestConfirmation("Delete this keyword? <b>This action cannot be undone.</b>", "Delete Keyword");

      if (confirmed) {
        const res = await keywordSvcRef.current.deleteKeyword(id, selectedPage.id);

        if (res) {
          setKeywords(prev => prev.filter(kw => kw.Id !== id));
        }
      }
    }
  };

  const treeNodeDefinition = (): Array<INodeTypeDefinition> => [
    {
      parentPropertyName: "parent",
      displayPropertyName: "text",
      typeIconDefinition: faFile,
      onItemSelect: handlePageSelect,
      allowDrop: false,
      nodeTypeValue: "page",
    },
    {
      parentPropertyName: "parent",
      displayPropertyName: "text",
      typeIconDefinition: faGlobe,
      onItemSelect: () => console.log("language selected"),
      allowDrop: false,
      nodeTypeValue: "lang",
    },
  ];

  // Set up validators for each main list item
  useEffect(() => {
    keywords.forEach(kw => {
      const formId = `KWFORM-${kw.Id}`;
      if (formRefs.current[formId] && !validatorsRef.current[formId]) {
        validatorsRef.current[formId] = createFormValidation(formRefs.current[formId]!, getValidationConfig());
      }
    });
  }, [keywords]);

  // Set up validator for modal form
  useEffect(() => {
    if (addKeyword && modalFormRef.current && !validatorsRef.current.NEWKEYWORD) {
      validatorsRef.current.NEWKEYWORD = createFormValidation(modalFormRef.current, getValidationConfig());
    }

    // Cleanup modal validator when modal closes
    return () => {
      if (!addKeyword && validatorsRef.current.NEWKEYWORD) {
        validatorsRef.current.NEWKEYWORD.destroy();
        delete validatorsRef.current.NEWKEYWORD;
      }
    };
  }, [addKeyword]);

  useEffect(() => {
    if (selectedPage) {
      getKeywords(selectedPage.id);
    }
  }, [selectedPage]);

  useEffect(() => {
    if (pages.length > 0 && pageParamId && !isNaN(Number(pageParamId))) {
      const page = pages.find(p => p.id.toString() === pageParamId);
      if (page) {
        setSelectedPage(page);
        EventBus.getInstance().publish(EventBus.Event.OnTreeNodeSelect, page.id.toString());
      }
    }
  }, [pages]);

  useEffect(() => {
    if (!contentSvcRef.current) {
      contentSvcRef.current = new ContentService();
    }

    if (!keywordSvcRef.current) {
      keywordSvcRef.current = new KeywordService();
    }

    const getData = async (): Promise<void> => {
      try {
        await getPages();
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, []);

  return (
    <>
      {loading && <Loading Message="loading page/keywords..." SpinSize="5x" />}
      {!loading && (
        <div className={styles.page}>
          <div className={styles.workspace}>
            <div className={styles.workspaceSidebar}>
              <div className={styles.treePanel}>
                <div className={styles.panelHeader}>
                  <div className={styles.panelTitleWrap}>
                    <h2 className={styles.panelTitle}>Your Site Pages</h2>
                    <p className={styles.panelSubtle}>Choose a page from the site tree to manage its keyword entries.</p>
                  </div>
                </div>
                <div className={styles.panelDivider} aria-hidden="true"></div>
                <div className={styles.treeBody}>
                  <div className={styles.treeShell} id="PagesTree">
                <GenericTreeView
                  items={pages}
                  closeOnSelect={false}
                  idPropertyName="id"
                  rootNodeKeyValue="#"
                  nodeTypeDefinitions={treeNodeDefinition()}
                  typePropertyName="type"
                  sortBy={TreeItemSort.Unsorted}
                />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.workspaceMain}>
            {selectedPage && (
              <div className={styles.detailPanel}>
                <div className={styles.detailHeader}>
                  <div>
                    <h2 className={styles.detailTitle}>Page Keywords</h2>
                    <p className={styles.detailSummary}>Manage keyword entries for the selected page and keep search-focused content descriptors organised.</p>
                    <div className={styles.selectedInfo}>
                      <span>Selected page</span>
                      <strong>{selectedPage.text}</strong>
                    </div>
                  </div>
                  <div className={styles.detailToolbar}>
                    <Button
                      variant="outline-primary"
                      className="actionButton"
                      title="add keyword"
                      onClick={addNewKeyword}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      <span>Add keyword</span>
                    </Button>
                  </div>
                </div>
                <div className={styles.detailBody}>
                {!keywords || keywords.length <= 0 ? (
                  <AppEmptyState
                    icon={faCircleInfo}
                    compact={true}
                    className={styles.emptyNotice}
                    title="No keywords found"
                    message={`No keyword entries have been added for ${selectedPage?.text ?? "this page"} yet.`}
                  />
                ) : (
                  <div className={styles.list}>
                    {keywords.map(kw => (
                      <Form
                        id={`KWFORM-${kw.Id}`}
                        key={`KWFORM-${kw.Id}`}
                        ref={(el: HTMLFormElement) => formRefs.current[`KWFORM-${kw.Id}`] = el}
                        className={styles.keywordCard}
                      >
                        <Row key={`KW-${kw.Id}`} className={styles.keywordCardRow}>
                          <Col md={12} xl={10}>
                            <FormLabel>Keyword</FormLabel>
                            <FormControl
                              type="text"
                              id="Keyword"
                              name="Keyword"
                              autoComplete="off"
                              value={kw.Keyword}
                              placeholder="add a keyword for SEO..."
                              onChange={(e) => handleUpdateKeyword(e.target.value, kw.Id)}
                            />
                          </Col>
                          <Col md={12} xl={2}>
                            <div className={styles.keywordActions}>
                            <Button
                              variant="success"
                              title="save keyword"
                              className="btn-nomin me-1"
                              onClick={() => saveKeyword(kw.Id)}>
                              <FontAwesomeIcon icon={faSave} />
                            </Button>
                            <Button
                              variant="danger"
                              title="remove keyword"
                              className="btn-nomin"
                              onClick={() => deleteKeyword(kw.Id)}>
                              <FontAwesomeIcon icon={faTimes} />
                            </Button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    ))}
                  </div>
                )}
                </div>
              </div>
            )}

            {!selectedPage && (
              <div className={styles.emptyPanel}>
                <div className={styles.emptyState}>
                  <h2 className={styles.emptyStateTitle}>No page selected</h2>
                  <p className={styles.emptyStateText}>
                    Choose a page from the tree to review and manage the keyword entries attached to it.
                  </p>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {addKeyword && selectedKeyword && (
        <AppModal
          onClose={cancelAddKeyword}
          onDataAction={() => saveKeyword()}
          showButtons={true}
          size="md"
          actionButtonVariant="success"
          cancelButtonVariant="primary"
          title="Add Keyword"
        >
          <Form id="NEWKEYWORD" ref={modalFormRef}>
            <Row>
              <Col md={12}>
                <FormLabel>Keyword</FormLabel>
                <FormControl
                  id="Keyword"
                  name="Keyword"
                  autoFocus={true}
                  autoComplete="off"
                  placeholder="Add a page keyword for SEO..."
                  value={selectedKeyword.Keyword}
                  onChange={handleNewKeywordChange}
                />
              </Col>
            </Row>
          </Form>
        </AppModal>
      )}
    </>
  );
};

export default PageKeywords;
